import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, NgZone, OnDestroy, PLATFORM_ID, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { environment } from '../../../environments/environment';
import { ContactService } from './contact.service';

/** Minimal shape of the Cloudflare Turnstile client injected by challenges.cloudflare.com/turnstile/v0/api.js */
interface TurnstileApi {
  render(
    container: string | HTMLElement,
    options: {
      sitekey: string;
      callback?: (token: string) => void;
      'error-callback'?: () => void;
      'expired-callback'?: () => void;
      theme?: 'auto' | 'light' | 'dark';
    }
  ): string;
  reset(widgetId?: string): void;
  remove(widgetId?: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/**
 * api.js is only needed on the pages that render a Turnstile widget, so it's loaded on demand
 * here rather than globally in index.html. Memoized module-level so navigating between this page
 * and Testimonials (which loads it the same way) doesn't inject the script tag twice.
 */
let turnstileScriptPromise: Promise<void> | null = null;

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) {
    return Promise.resolve();
  }
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load the Turnstile script.'));
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
}

@Component({
  selector: 'app-contact',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, MatIconModule],
  templateUrl: './contact.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./contact.scss'],
})
export class Contact implements AfterViewInit, OnDestroy {
  private readonly fb = inject(FormBuilder);
  private readonly contactService = inject(ContactService);
  private readonly zone = inject(NgZone);

  readonly submitting = signal(false);
  readonly submitted = signal(false);
  readonly errorMessage = signal<string | null>(null);
  /** The Turnstile token; empty until the widget solves. Guards submit. */
  readonly captchaToken = signal('');
  readonly captchaReady = signal(false);

  private widgetId: string | null = null;
  private pollHandle: ReturnType<typeof setTimeout> | null = null;
  private readonly isBrowser: boolean;

  readonly form = this.fb.nonNullable.group({
    name: ['', [Validators.required, Validators.maxLength(120)]],
    email: ['', [Validators.required, Validators.email, Validators.maxLength(254)]],
    subject: ['', [Validators.required, Validators.maxLength(200)]],
    message: ['', [Validators.required, Validators.maxLength(5000)]],
  });

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    // Turnstile is a browser-only widget; never touch window during SSR/prerender.
    if (this.isBrowser) {
      loadTurnstileScript().catch(() => {});
      this.renderCaptchaWhenReady();
    }
  }

  ngOnDestroy(): void {
    if (this.pollHandle) {
      clearTimeout(this.pollHandle);
    }
    if (this.isBrowser && this.widgetId && window.turnstile) {
      window.turnstile.remove(this.widgetId);
    }
  }

  hasError(control: keyof typeof this.form.controls): boolean {
    const c = this.form.controls[control];
    return c.invalid && (c.touched || c.dirty);
  }

  submit(): void {
    this.errorMessage.set(null);

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (!this.captchaToken()) {
      this.errorMessage.set('Please complete the "I\'m human" check before sending.');
      return;
    }
    if (this.submitting()) {
      return;
    }

    this.submitting.set(true);
    this.contactService
      .submit({ ...this.form.getRawValue(), captchaToken: this.captchaToken() })
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.submitted.set(true);
        },
        error: () => {
          this.submitting.set(false);
          this.errorMessage.set(
            'Sorry, we could not send your message. Please complete the human-verification check and try again.'
          );
          this.resetCaptcha();
        },
      });
  }

  /**
   * The api.js script loads async, so window.turnstile may not exist yet when this component
   * mounts. Poll for it (same approach the site uses for the GoatCounter badge), then render
   * the widget explicitly so we control exactly where it appears.
   */
  private renderCaptchaWhenReady(): void {
    if (window.turnstile) {
      this.renderCaptcha(window.turnstile);
      return;
    }
    this.pollHandle = setTimeout(() => this.renderCaptchaWhenReady(), 300);
  }

  private renderCaptcha(turnstile: TurnstileApi): void {
    this.widgetId = turnstile.render('#contact-turnstile', {
      sitekey: environment.turnstileSiteKey,
      theme: 'light',
      // Turnstile callbacks fire outside Angular's zone; re-enter so signals refresh the view.
      callback: (token: string) => this.zone.run(() => this.captchaToken.set(token)),
      'expired-callback': () => this.zone.run(() => this.captchaToken.set('')),
      'error-callback': () => this.zone.run(() => this.captchaToken.set('')),
    });
    this.captchaReady.set(true);
  }

  private resetCaptcha(): void {
    this.captchaToken.set('');
    if (this.isBrowser && this.widgetId && window.turnstile) {
      window.turnstile.reset(this.widgetId);
    }
  }
}
